/**
 * BridgeLostBanner - says out loud why every control just went dead.
 *
 * THE INCIDENT THIS EXISTS FOR. On 2026-09-10, two hours into a five hour
 * set, the rig stopped responding. Every control was unpressable, the BPM was
 * stuck, the logo dot kept pulsing. The operator read that as "the app froze"
 * and force-quit it.
 *
 * That is EXACTLY what a completely healthy app looks like when it loses its
 * bridge, because `FireButton` is `disabled={!live()}` and `live()` requires a
 * connected bridge (DEC-124 Clause A). Reproduced deliberately in
 * `_probe_bridge_loss_symptom.ts`: cut the link and every control greys, the
 * BPM stops, the dot keeps pulsing, and the app is fine the whole time.
 *
 * So the app already KNEW. It just never said so anywhere the operator was
 * looking. The header pill does change to "offline" / "0/2 up", but it is a
 * small chip in the chrome bar while the thing commanding attention is a
 * screenful of dead buttons with no stated reason. Under stage light, mid
 * set, that reads as a crash.
 *
 * WHAT THIS FIXES, and what it deliberately does not. It does not repair the
 * connection - the reconnect machinery was measured healing a full 142 second
 * outage in ~18 seconds and is deliberately untouched. It removes the
 * AMBIGUITY: a dead-looking rig now states whether it is disconnected and
 * trying, which is the difference between "wait a moment" and "force-quit
 * mid-song".
 *
 * The elapsed counter is load-bearing, not decoration. A number that visibly
 * advances is proof the JavaScript is running, which is the very question
 * three sessions failed to answer from outside the device. If this banner is
 * on screen and its timer is climbing, the app is alive and the network is
 * the problem. If it is on screen and FROZEN, the page itself has stopped -
 * and that is the first time that distinction has been visible from the front
 * of the room.
 *
 * Renders only while disconnected with at least one bridge configured, so it
 * adds nothing to the normal path. A never-paired rig is a different state
 * with its own existing empty-state copy, and is not hijacked here.
 */

import { Show, createEffect, createSignal, onCleanup } from 'solid-js';
import { bridges, bridgeConnectedReactive, getConnection, connectionsVersion } from '../opbridge/store.ts';
import { commsIssues } from '../opbridge/comms.ts';
import { openConnect } from '../settings/store.ts';

export function BridgeLostBanner() {
  // Ticks once a second purely so `elapsed` re-renders. This is the liveness
  // proof: if the number stops moving, the page is dead rather than offline.
  const [now, setNow] = createSignal(Date.now());
  const t = setInterval(() => setNow(Date.now()), 1000);
  onCleanup(() => clearInterval(t));

  let lostAt: number | null = null;

  // PLAIN FUNCTIONS, not createMemo, and that is load-bearing. The memo
  // version of this read `true` forever: measured in one page at one instant,
  // the canvas Button was `disabled=true` while this still said connected, so
  // the banner stayed hidden on a rig whose every control was dead. A memo
  // caches and keeps its subscription to whatever it read first, and the
  // connection object is REPLACED in `connections` on a disconnect - so the
  // memo sat subscribed to a dead object's signal that would never change
  // again. FireButton has always used plain arrows (`const live = () => ...`)
  // for exactly this path; matching that idiom is what makes the banner and
  // the controls agree. See the project's "Solid: Map read tracks nothing"
  // note - a Map lookup is not a tracked dependency.
  const configured = () => bridges().length;

  // REUSE the predicate items use, never a reimplementation of it. The first
  // version of this read `getConnection(id).statusSignal()()` by hand and
  // disagreed with the controls in exactly the state that matters: measured
  // 27s into a full outage, every control was disabled while this still said
  // connected, so the banner stayed hidden on a dead rig. `store.ts` says of
  // `bridgeConnectedReactive` that an item's "am I live" indicator and its
  // actual send target "can never disagree" - that guarantee only holds for
  // callers who go through it, which is now what this does.
  //
  // Also deliberately NOT `anyConnectedReactive()`: despite the name it checks
  // only the PRIMARY, so on a mesh it would cry "bridge lost" while a peer was
  // still connected and firing. A banner that lies about a working rig is
  // worse than no banner.
  // `connectionsVersion()` is read FIRST, and that is the whole trick. It is
  // the store's own documented remedy: "callers that need to react to a swap
  // should read this signal before calling getConnection(), purely to register
  // the dependency". Without it this read sticks forever, because
  // `bridgeConnectedReactive` ends in `connections.get(id)` - a Map lookup,
  // which Solid cannot track - and the connection OBJECT is REPLACED on a
  // reconnect, leaving any subscription pointed at a dead object's signal.
  //
  // Measured, not theorised: before this line the banner read `connected` for
  // 147 seconds of total network loss while the console showed
  // `[opbridge] connected -> unreachable` and every control was disabled.
  const connected = () => {
    connectionsVersion();
    return bridges().some((b) => bridgeConnectedReactive(b.id));
  };

  const show = () => configured() > 0 && !connected();

  const clock = (secs: number) => {
    if (secs < 60) return `${secs}s`;
    const m = Math.floor(secs / 60);
    return `${m}m ${String(secs % 60).padStart(2, '0')}s`;
  };
  const elapsed = () => {
    now(); // track the tick
    if (!show()) { lostAt = null; return ''; }
    if (lostAt === null) lostAt = Date.now();
    return clock(Math.max(0, Math.round((Date.now() - lostAt) / 1000)));
  };

  // Comms (2026-11-04, decided on public/comms-study.html: "every problem"): with the rig at least partly up, the
  // same banner says the worst live problem, how many more there are, and since when; a tap opens Computers, where
  // they are kept. Everything lost stays BRIDGE LOST above, unchanged.
  const problems = () => commsIssues().filter((i) => i.level !== 'info');
  const worst = () => (show() ? null : problems()[0] ?? null);
  const since = () => { now(); const w = worst(); return w ? clock(Math.max(0, Math.round((Date.now() - w.since) / 1000))) : ''; };

  // The page MOVES DOWN under either banner (operator, 2026-11-05: "have the page move down", then "have every banner
  // push the page down"), so it covers no control: the canvas shifts by the banner's height and shrinks by the same,
  // so its bottom row stays on screen. It is a transform on `.scaled-canvas-inner`, whose rendered/layout ratio is
  // what drag and resize read their scale from (EditableItem), and its clientWidth/Height (the usable bounds) do not
  // change. This replaces BRIDGE LOST's "the layout must not move because the network did" by the operator's choice.
  let commsEl: HTMLDivElement | undefined;
  let lostEl: HTMLDivElement | undefined;
  createEffect(() => {
    const el = show() ? lostEl : worst() ? commsEl : undefined;
    if (!el) return;
    const host = el.closest('.canvas-placeholder') as HTMLElement | null;
    if (!host) return;
    const apply = () => {
      const H = el.offsetHeight, h = host.clientHeight;
      if (!H || !h || H >= h) return;
      host.style.setProperty('--comms-push', `${H}px`);
      host.style.setProperty('--comms-k', String((h - H) / h));
      host.dataset.commsPush = 'true';
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    ro.observe(host);
    onCleanup(() => {
      ro.disconnect();
      delete host.dataset.commsPush;
      host.style.removeProperty('--comms-push');
      host.style.removeProperty('--comms-k');
    });
  });

  // The host always renders (`display: contents`, so it costs no layout) and
  // publishes the two inputs to its own decision. Without this, "the banner
  // did not appear" is indistinguishable from "the component never mounted",
  // and a probe has to guess which - which is exactly what happened the first
  // time this was tested. Now the decision is readable from the DOM.
  return (
    <div
      class="bridge-lost-host"
      data-configured={configured()}
      data-connected={connected() ? 'true' : 'false'}
      data-show={show() ? 'true' : 'false'}
      data-default-live={String(bridgeConnectedReactive(undefined))}
      data-raw-status={bridges().map((b) => getConnection(b.id)?.statusSignal()() ?? 'no-conn').join('|')}
    >
    <Show when={show()}>
      <div ref={lostEl} class="bridge-lost" role="status" aria-live="polite">
        <span class="bridge-lost-title">BRIDGE LOST</span>
        <span class="bridge-lost-detail">
          Controls are disabled because nothing is connected. Reconnecting.
        </span>
        {/* Monospaced + tabular so the digits do not jitter, which would make
            a running clock look like a stuttering one. */}
        <span class="bridge-lost-elapsed">{elapsed()}</span>
      </div>
    </Show>
    <Show when={worst()}>
      {(w) => (
        <div ref={commsEl} class="bridge-lost comms-banner" role="status" aria-live="polite" data-tone={w().level === 'bad' ? 'bad' : 'warn'}
          data-issue={w().key} onClick={openConnect} title="Open Computers">
          <span class="bridge-lost-title">{w().title}</span>
          <span class="bridge-lost-detail">{w().text}</span>
          <Show when={problems().length > 1}><span class="comms-banner-more">+{problems().length - 1} more</span></Show>
          <span class="bridge-lost-elapsed">{since()}</span>
          <span class="comms-banner-go">Computers ›</span>
        </div>
      )}
    </Show>
    </div>
  );
}
